import loadable from "@loadable/component";
import * as wjcGridXlsx from "@grapecity/wijmo.grid.xlsx";
import _ from "lodash";
import NotFoundPage from "./NotFoundPage";
import styles from "e3-components/components/InputControls/E3Button.module.css";

export const componentLoader = (programPath) => {
  // const component = loadable(() => import( /* @vite-ignore */ `@/pages${programPath}`));
  const modules = import.meta.glob("pages/**/*.jsx");
  const envMode = import.meta.env.MODE == "useLocal" ? true : false;

  const normalizedPath = programPath.replace(/\/+/g, "/").replace(/^\//, "");
  const fullPath = `/src/pages/${normalizedPath}`; // 절대 경로로 변경

  if (!modules[`${fullPath}.jsx`]) return () => <NotFoundPage />;

  const component = envMode
    ? loadable(() => import(/* @vite-ignore */ fullPath))
    : loadable(() => modules[`${fullPath}.jsx`]());

  return component;
};

/**
 * 파라미터가 배열인지 아닌지 체크
 * 배열: true
 * 그외: false
 * @param {*} arr
 * @returns
 */
const chkArray = (arr) => {
  if (arr && arr.length > 0) {
    return true;
  } else {
    return false;
  }
};

/**
 * grid multi sheet excel download
 * @param {*} arrGrid
 * @param {*} colHeader
 * @param {*} style
 * @param {*} fileName
 * @param {*} newArrData
 * @param {*} isCheckedItem
 */
export const saveGridExcel = (
  arrGrid = [],
  colHeader = true,
  style = true,
  fileName = "",
  newArrData = [],
  isCheckedItem = false
) => {
  if (chkArray(arrGrid)) {
    let excelData = "";
    arrGrid.forEach((x, idx) => {
      const flexGrid = x.flexGrid.current.refGrid.current;

      // copy grid data
      const copyGrid = _.cloneDeep(flexGrid);

      if (newArrData.length) {
        copyGrid.itemsSource = newArrData;
      }

      if (isCheckedItem) {
        // expect not checking
        const trueValue = copyGrid.itemsSource.filter((x) => {
          return x.ROW_CHECK === true;
        });

        copyGrid.itemsSource = trueValue;
      }

      // grid data -> excel data
      let _xlsx = wjcGridXlsx.FlexGridXlsxConverter.saveAsync(
        copyGrid,
        {
          includeColumnHeaders: colHeader,
          includeStyles: style,
          includeColumns: function (column) {
            return (
              column.binding !== "ROW_CHECK" &&
              column.binding !== "ROW_STATUS" &&
              column.visible
            );
          },
        },
        null,
        null,
        (progress) => (this.progressBar.value = progress), //진행률표시기
        true
      );

      // data stack for excel export
      if (!excelData) {
        excelData = _xlsx;
      } else {
        excelData.sheets[idx] = _xlsx.sheets[0];
      }

      // modify sheet name
      if (x.sheetName) excelData.sheets[idx].name = x.sheetName;

      // refresh data source
      copyGrid.itemsSource = flexGrid.itemsSource;

      // refresh height value
      copyGrid.onScrollPositionChanged();
    });

    // exec excel export
    if (excelData)
      excelData.saveAsync(fileName > "" ? fileName : "FlexGrid.xlsx");
  } else {
    console.error("옳바른 배열 형태가 아닙니다.");
  }
};
/**
 * @typedef {object} ComponentOption
 * @property {"IMAGE"|"BUTTON"|"LINK"|"TEXT"|"DROPDOWN"|undefined} type - default BUTTON
 * @property {object | undefined} data - payload
 * @property {(e: MouseEvent, data: object) => void | undefined} onClick - clickEvent
 * @property {(e: Event, data: object) => void | undefined} onChange - changeEvent
 * @property {Object<string, string> | undefined} style - inline 스타일 (ex. { color: "red" })
 * @property {string | undefined} className - 클래스 이름
 * @property {string | undefined} src - Image 전용
 * @property {string | undefined} alt - Image 전용
 * @property {string | undefined} href - Link 전용
 * @property {string | undefined} target - Link 전용
 * @property {string | undefined} text - Button | Link | Text 전용
 * @property {string | undefined} buttonType
 * @property {string | undefined} colorType
 * @property {string | undefined} size
 * @property {string | undefined} iconType
 * @property {Array<{value: string, text: string}> | undefined} options - Dropdown 전용
 * @property {string | undefined} value - Dropdown 선택값
 *
 */

/**
 * 셀에 컴포넌트를 세팅
 * @param {HTMLElement} cell
 * @param {ComponentOption} option
 */
export const setCellComponent = (cell, option) => {
  // Element 초기화
  cell.innerHTML = "";

  const element = createComponentElement(option);

  applyCommonAttributes(element, option);

  cell.appendChild(element);
};

/**
 * type에 따라 HTML 요소를 생성
 * @param {ComponentOption} option
 * @returns {HTMLElement}
 */
function createComponentElement(option = {}) {
  let el;
  const { type } = option;
  switch (type) {
    case "IMAGE":
      el = document.createElement("img");
      el.src = option.src ?? "";
      el.alt = option.alt ?? "";
      el.style.maxWidth = "100%";
      break;

    case "LINK":
      el = document.createElement("a");
      el.href = option.href ?? "#";
      el.textContent = option.text ?? "Link";
      el.target = option.target ?? "_blank";
      break;

    case "BUTTON":
      el = document.createElement("button");
      el.textContent = option.text ?? "Button";
      el.type = "button";
      break;
    case "DROPDOWN":
      el = document.createElement("select");
      (option.options ?? []).forEach((item) => {
        const optionEl = document.createElement("option");
        optionEl.value = item.value ?? "";
        optionEl.textContent = item.text ?? "";
        el.appendChild(optionEl);
      });
      el.value = option.value ?? "";
      break;
    case "TEXT":
    default:
      el = document.createElement("p");
      el.textContent = option.text ?? "input text property...";
      break;
  }

  return el;
}

/**
 * 공통 속성 및 이벤트 설정
 * @param {HTMLElement} el
 * @param {ComponentOption} option
 */
function applyCommonAttributes(el, option) {
  const { data = {}, onClick, onChange, style = {}, className, type } = option;

  el.style.cursor = type === "TEXT" ? "default" : "pointer";
  el.style.margin = "4px";


  // className 설정
  if (className) {
    el.className = className;
  }
  if (type === "BUTTON") {
    el.className += ` ${_makeClassnames({
      buttonType: option.buttonType,
      iconType: option.iconType,
      size: option.size,
      colorType: option.colorType,
    })}`;
  }

  // 이벤트 핸들러 등록
  if (onClick) {
    el.addEventListener("click", (e) => onClick(e, data));
  }
  if (type === "DROPDOWN") {
    el.addEventListener("mousedown", (e) => e.stopPropagation());
    el.addEventListener("click", (e) => e.stopPropagation());
    if (onChange) {
      el.addEventListener("change", (e) => onChange(e, data));
    }

    if (Object.keys(style).length === 0) {
      style.width = "100%";
      style.height = "28px";
      style.border = "1px solid #ddd";
      style.borderRadius = "3px";
      style.backgroundColor = "#fff";
    } 
  }

  // 사용자 지정 스타일 적용
  Object.assign(el.style, style);
}
const _makeClassnames = ({ buttonType, colorType, size, iconType }) => {
  const classNames = [];
  classNames.push(styles.btn);

  switch (buttonType) {
    case "default":
      break;
    case "close":
      classNames.push(styles.closeBtn);
      break;
    case "search":
      classNames.push(styles.searchBtn);
      break;
    case "init":
      classNames.push(styles.initBtn);
      break;
  }

  if (buttonType === "default") {
    switch (colorType) {
      case "default":
        classNames.push(styles.colorDefault);
        break;
      case "default-outline":
        classNames.push(styles.colorDefaultOutline);
        break;
      case "main":
        classNames.push(styles.colorMain);
        break;
      case "main-outline":
        classNames.push(styles.colorRedMain);
        break;
      case "sub1":
        classNames.push(styles.colorSub1);
        break;
      case "sub1-outline":
        classNames.push(styles.colorSub1Outline);
        break;
      case "sub2":
        classNames.push(styles.colorSub2);
        break;
      case "sub2-outline":
        classNames.push(styles.colorSub2Outline);
        break;
      case "sub3":
        classNames.push(styles.colorSub3);
        break;
      case "sub3-outline":
        classNames.push(styles.colorSub3Outline);
        break;
      case "sub4":
        classNames.push(styles.colorSub4);
        break;
      case "sub5":
        classNames.push(styles.colorSub5);
        break;
      case "sub6":
        classNames.push(styles.colorSub6Line);
        break;
      case "sub7Green":
        classNames.push(styles.sub7GreenLine);
        break;
      case "pageOn":
        classNames.push(styles.pageOn);
        break;
      case "pageDefault":
        classNames.push(styles.pageDefault);
        break;
      case "prevList":
        classNames.push(styles.prevList);
        break;
      case "colorNoBorder":
        classNames.push(styles.colorNoBorder);
        break;
      case "roundRed":
        classNames.push(styles.roundRed);
        break;
    }
  }

  switch (size) {
    case "default":
      break;
    case "small":
      classNames.push(styles.small);
      break;
    case "middle":
      classNames.push(styles.middle);
      break;
    case "large":
      classNames.push(styles.large);
      break;
  }

  if (iconType) {
    classNames.push(styles.icon);
    classNames.push(styles[iconType]);
  }

  return classNames.join(" ");
};
